import { motion } from "framer-motion";
import { BookOpen, Globe2, Users, HeartHandshake, ArrowRight } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import { useNavigate } from "react-router-dom";

const METODO_PATH: Record<string, string> = {
  es: "/metodologia-montessori",
  en: "/montessori-education",
  pt: "/metodologia-montessori",
  fr: "/methode-montessori",
};

const METODO_LABEL: Record<string, string> = {
  es: "Descubre el Método Gándara",
  en: "Discover the Gándara Method",
  pt: "Descobre o Método Gándara",
  fr: "Découvrez la méthode Gándara",
};

export const HowWeTeach = () => {
  const { translations, language } = useLanguage();
  const t = translations?.howWeTeach;
  const navigate = useNavigate();

  const methodologies = [
    { 
      icon: BookOpen, 
      key: "active" as const,
      gradient: "from-primary/20 to-primary/5"
    },
    { 
      icon: Globe2, 
      key: "global" as const,
      gradient: "from-accent/20 to-accent/5"
    },
    { 
      icon: Users, 
      key: "inclusion" as const,
      gradient: "from-secondary/20 to-secondary/5"
    },
    { 
      icon: HeartHandshake, 
      key: "evaluation" as const,
      gradient: "from-muted/40 to-muted/10"
    },
  ];

  if (!t) return null;

  return (
    <section className="py-20 px-4 bg-muted/30">
      <div className="container mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="section-title">{t.title}</h2>
          <p className="section-subtitle">{t.subtitle}</p>
        </motion.div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto">
          {methodologies.map((methodology, index) => (
            <motion.div
              key={methodology.key}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              viewport={{ once: true }}
              className="feature-card group hover:shadow-lg transition-all duration-300"
            >
              <div className={`w-full h-2 bg-gradient-to-r ${methodology.gradient} rounded-t-lg mb-6`} />
              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0">
                  <div className="w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center group-hover:bg-primary/20 transition-colors duration-300">
                    <methodology.icon className="w-7 h-7 text-primary" />
                  </div>
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-foreground mb-2">
                    {t.items[methodology.key].title}
                  </h3>
                  <p className="text-muted-foreground text-sm leading-relaxed">
                    {t.items[methodology.key].description}
                  </p>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        <div className="text-center mt-12">
          <button
            onClick={() => navigate(`/${language}${METODO_PATH[language] ?? METODO_PATH.es}`)}
            className="inline-flex items-center gap-2 bg-primary text-primary-foreground hover:bg-primary/90 transition-colors font-medium px-8 py-3 rounded-full shadow-md hover:shadow-lg"
          >
            {METODO_LABEL[language] ?? METODO_LABEL.es}
            <ArrowRight className="h-4 w-4" />
          </button>
        </div>
      </div>
    </section>
  );
};
